import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { capitalize, some } from 'lodash'
import dayjs from 'dayjs'
import {
  ProfileField,
  ProfilePicture,
} from '../../../../../../components/generic'
import { colors } from '@lmc/common/constants'
import { isBrowser, isTablet } from 'react-device-detect'
import { Badge } from '@lmc/ui-kit'
import LpsExpiresSoon from '@lmc/ui-kit/src/atoms/Icon/customShapes/LpsExpiresSoon'
import DolsExpiresSoon from '@lmc/ui-kit/src/atoms/Icon/customShapes/DolsExpiresSoon'
import Lps from '@lmc/ui-kit/src/atoms/Icon/customShapes/Lps'
import Dols from '@lmc/ui-kit/src/atoms/Icon/customShapes/Dols'

const isDesktop = isBrowser || isTablet

const isDolsAboutToExpireIn2Weeks = (endDate) => {
  if (!endDate) {
    return false
  }
  const end = dayjs(endDate)
  const today = dayjs()
  const daysUntil = end.diff(today, 'days')
  return daysUntil >= 0 && daysUntil <= 14
}

const hasDolsExpired = (endDate) => {
  if (!endDate) {
    return false
  }
  const end = dayjs(endDate)
  const today = dayjs()
  const daysUntil = end.diff(today, 'days')
  return daysUntil < 0
}

const getDolsAlert = (profile) => {
  if (
    profile.dolsReviewDate &&
    dayjs(profile.dolsReviewDate).isBefore(dayjs())
  ) {
    return 'Overdue'
  }
}

const getDolsDates = (reviewDate, applicationDate) => {
  if (!reviewDate && !applicationDate) return null
  const result = []
  if (applicationDate)
    result.push(`Applied for ${dayjs(applicationDate).format('Do MMMM YYYY')}`)
  if (reviewDate)
    result.push(`Review ${dayjs(reviewDate).format('Do MMMM YYYY')}`)
  return result.join(' | ')
}

export const showLps = (countryCode) => ['ENG', 'WSL'].includes(countryCode)

export const getDolsIcon = (isDolsEnabled, endDate, countryCode) => {
  if (!isDolsEnabled) {
    return null
  }
  const isDolsAboutToExpire = isDolsAboutToExpireIn2Weeks(endDate)
  const isDolsExpired = hasDolsExpired(endDate)
  const isEnglandOrWalesOrScotland = showLps(countryCode)

  if (isDolsExpired) {
    return null
  }
  if (isDolsAboutToExpire) {
    return isEnglandOrWalesOrScotland ? (
      <LpsExpiresSoon width={58} height={22} endDate={endDate} />
    ) : (
      <DolsExpiresSoon width={58} height={22} endDate={endDate} />
    )
  }
  return isEnglandOrWalesOrScotland ? (
    <Lps width={48} height={22} />
  ) : (
    <Dols width={58} height={22} />
  )
}

export default class ResidentPrimaryInfo extends React.Component {
  getDateOfBirthValue = (profile) => {
    const birthday = dayjs(profile.dateOfBirth).format('Do MMMM YYYY')
    return (
      <span>
        {birthday} <span style={styles.highlightText}>({profile.age})</span>
      </span>
    )
  }

  getBoolDisplayValue = (value) => {
    if (value === true) {
      return 'Yes'
    }
    if (value === false) {
      return 'No'
    }
  }

  getZoneDisplayValue = (features, value) => {
    if (!features.pro) {
      return (
        <Link to="/pro-advert">
          Pro! Feature <span className="octicon octicon-chevron-right" />
        </Link>
      )
    }
    if (value) {
      return value
    }
    return 'Unassigned'
  }

  isShowingLocation = (location) => {
    if (!location) return false
    const hasInfo = (key) => !!location[key].length
    const keys = Object.keys(location)
    return !!keys.length && some(keys, hasInfo)
  }

  renderLocation = (isShowingLocation, location) => {
    if (!isShowingLocation) return null
    const labels = ['building', 'floor', 'room', 'bed']

    return (
      <div style={styles.locationContainer}>
        {labels.map((label, i) => {
          if (!location[label]) return null

          return (
            <div key={i} style={styles.locationSubContainer}>
              <div style={styles.locationLabel}>{capitalize(label)}</div>
              <div style={styles.locationValue}>{location[label]}</div>
            </div>
          )
        })}
      </div>
    )
  }

  renderNames = (profile) => {
    const displayName = profile?.preferredName
      ? profile.preferredName
      : profile.name.first

    return (
      <div style={styles.namesContainer}>
        <span style={styles.displayName}>{displayName}</span>
        <span style={styles.basicInfoText}>
          {`${profile.name.first} ${profile.name.last}`}
        </span>
      </div>
    )
  }

  renderBasicInfo = (features, profile) => {
    const dateOfBirth = this.getDateOfBirthValue(profile)
    const dnacpr = this.getBoolDisplayValue(profile.dnacpr)
    let dnacprDisplayText
    if (dnacpr === 'Yes') {
      dnacprDisplayText = 'Do Not Attempt CPR'
    } else if (dnacpr === 'No') {
      dnacprDisplayText = 'Attempt CPR'
    }
    const dols = this.getBoolDisplayValue(profile.dols)
    const dolsSecondaryText = getDolsDates(
      profile.dolsReviewDate,
      profile.dolsApplicationDate
    )
    const dolsIcon = getDolsIcon(
      profile.dols,
      profile.dolsEndDate,
      features.countryCode
    )
    const isShowingLocation = this.isShowingLocation(profile.location)
    const zone = this.getZoneDisplayValue(features, profile.zoneTitle)
    const hasCarePlans = !!features.carePlans
    const isCanada = features.countryCode === 'CA'
    const isDummy = profile?.status === 'dummy'

    return (
      <div style={styles.basicInfoContainer}>
        {this.renderNames(profile)}
        {isDummy && (
          <Badge
            color={colors.orange}
            style={styles.dummyBadge}
            textColor={colors.white}
            text="Example"
          />
        )}
        <ProfileField
          label="Date of birth"
          style={{
            ...styles.infoField,
            marginTop: 18,
          }}
          value={dateOfBirth}
        />
        {!hasCarePlans && (
          <ProfileField
            label="Gender"
            style={styles.infoField}
            value={capitalize(profile.gender)}
          />
        )}
        <ProfileField
          label="Status"
          style={styles.infoField}
          value={capitalize(
            profile.status === 'pre-assessment'
              ? 'Initial Assessment'
              : profile.status
          )}
        />
        <ProfileField label="Zone" style={styles.infoField} value={zone} />
        {hasCarePlans && !isCanada && (
          <ProfileField
            badgeText={profile.dnacpr ? 'DNACPR' : null}
            badgeColor={colors.turquoise}
            label="DNACPR"
            style={styles.infoField}
            value={dnacprDisplayText}
            secondaryValue={profile.dnacprDetails}
          />
        )}
        {hasCarePlans && !isCanada && (
          <ProfileField
            alert={getDolsAlert(profile)}
            label={
              showLps(features.countryCode)
                ? 'Liberty Protection Safeguard (LPS)'
                : 'Deprivation of liberty safeguards (DoLS)'
            }
            style={{ ...styles.infoField, height: 50 }}
            value={dols}
            secondaryValue={dolsSecondaryText}
            icons={dolsIcon}
          />
        )}
        {/* Outputs the allergies (if any) of the resident. Has one of four 
                possible states: 
                1. The resident has no information entered for their allergies. In 
                this case the field will display No Information Entered
                2. The resident has a value of 'Yes' for hasAllergies. The field 
                displays the allergies AND a badge indicating that the resident has 
                allergies.
                3. The resident has a value of No for hasAllergies. The field displays
                No and no badge.
                4. The resudent has a value of Unknown for hasAllergies. The field 
                displays Unknown and no badge. */}
        {hasCarePlans && (
          <ProfileField
            badgeText={profile.hasAllergies === 'Yes' ? 'Allergies' : null}
            badgeColor={colors.orange}
            label="Allergies"
            style={styles.infoField}
            value={
              profile.hasAllergies
                ? profile.hasAllergies === 'Yes'
                  ? profile.allergies
                  : profile.hasAllergies
                : null
            }
          />
        )}
        {this.renderLocation(isShowingLocation, profile.location)}
      </div>
    )
  }

  render() {
    const { features, profile } = this.props
    const isShowingSummary = profile.summary && profile.summary.length

    return (
      <div style={{ paddingTop: isDesktop && !features.carePlans ? 50 : null }}>
        <ProfilePicture
          type="large"
          style={styles.image}
          url={profile.picture}
        />
        <div className="lmc-card" style={styles.basicInfoBox}>
          {this.renderBasicInfo(features, profile)}
          {isShowingSummary && <div style={styles.divider} />}
          {isShowingSummary && (
            <div style={styles.summaryContainer}>
              <span style={styles.summaryTitle}>Notes</span>
              <p style={styles.summaryText}>{profile.summary}</p>
            </div>
          )}
        </div>
      </div>
    )
  }
}

const styles = {
  basicInfoBox: {
    paddingTop: 80,
    paddingBottom: 25,
    position: 'relative',
    bottom: 24,
  },
  basicInfoContainer: {
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
  },
  basicInfoText: {
    color: colors.darkgrey,
    fontSize: 18,
  },
  displayName: {
    fontWeight: 600,
    fontSize: 28,
  },
  divider: {
    background: colors.grey,
    height: 1,
    margin: 'auto',
    marginTop: 12,
    marginBottom: 12,
    width: '100%',
  },
  highlightText: {
    color: 'black',
  },
  image: {
    position: 'relative',
    zIndex: 1,
    marginBottom: '-40px',
    marginTop: 20,
  },
  infoField: {
    textAlign: 'center',
  },
  locationContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 10,
    textAlign: 'center',
  },
  locationLabel: {
    color: colors.darkgrey,
    fontSize: 11,
  },
  locationSubContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: '10px 5px 10px 5px',
  },
  locationValue: {
    backgroundColor: colors.blue,
    borderRadius: 1000,
    color: 'white',
    padding: '2px 20px 2px 20px',
    fontSize: 14,
    float: 'left',
    marginTop: 2,
  },
  namesContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  summaryContainer: {
    flex: 1,
  },
  summaryTitle: {
    display: 'block',
    fontWeight: 'bold',
    fontSize: 18,
    margin: '12px 0px',
  },
  summaryText: {
    whiteSpace: 'pre-wrap',
  },
  dummyBadge: {
    width: 'max-content',
    marginTop: 5,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}

ResidentPrimaryInfo.propTypes = {
  features: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
}
